import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import {
  AddSupportTicketAPI,
  DeleteSupportTicketImageOnS3,
  UpdateSupportTicketAPI,
  UploadSupportTicketImageOnS3,
} from "src/DAL/SupportTicket/SupportTicket";
import { gr } from "src/design-system/tokens";
import GrTextField from "src/design-system/components/GrTextField";
import GrButton from "src/design-system/components/GrButton";
import GrAttachments from "src/design-system/components/GrAttachments";
import { useComposerTiles } from "./ticketHelpers";

export default function AddOrUpdateTickets({
  onCloseDrawer = () => {},
  editData,
  formType,
  // Called after a new ticket is saved (the list page reloads its Open list).
  onAdded = () => {},
  dataList,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [oldImagesArray, setOldImagesArray] = useState([]);
  const [state, setState] = useState({
    subject: "",
    description: "",
    ticket_id: "",
  });

  const handleRemove = (index) => {
    files.splice(index, 1);
    setFiles([...files]);
  };

  const handleUpload = (event) => {
    let setImageObject = {};
    const fileList = event.target.files;
    const arr = [];

    for (let i = 0; i < fileList.length; i++) {
      setImageObject = {
        path: fileList[i],
        type: "file",
      };

      arr.push(setImageObject);
    }
    setFiles((prevFiles) => [...prevFiles, ...arr]);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const UploadImages = async (formData) => {
    const result = await UploadSupportTicketImageOnS3(formData);
    return result.image_path;
  };

  const DeleteImages = async (formData) => {
    const result = await DeleteSupportTicketImageOnS3(formData);
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const results = files?.map((image) => {
      const formData = new FormData();
      formData.append("image", image.path);
      const result = UploadImages(formData);
      return result;
    });

    Promise.all(results).then(async (img_results) => {
      var setImageArray = [];
      img_results?.map((image_path) => {
        setImageArray.push(image_path);
      });
      const formDataObject = {
        subject: state.subject,
        description: state.description,
      };
      if (files.length > 0) {
        formDataObject.ticket_images = setImageArray;
      }
      const result = await AddSupportTicketAPI(JSON.stringify(formDataObject));
      if (result.code === 200) {
        onAdded();
        onCloseDrawer();
        setIsLoading(false);
        enqueueSnackbar(result.message, { variant: "success" });
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setIsLoading(false);
      }
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const deleted_images_array = [];
    let deleted_images_object = {};

    oldImagesArray.map((old_image) => {
      const filtered_array = files.filter(
        (image) => image.thumbnail_1 == old_image.thumbnail_1
      );
      delete old_image.type;
      if (filtered_array.length == 0) {
        deleted_images_array.push(old_image);
      }
    });

    if (deleted_images_array.length > 0) {
      deleted_images_object = {
        moment_image: deleted_images_array,
      };
      DeleteImages(deleted_images_object);
    }

    const results = files?.map((image) => {
      if (image.type === "file") {
        const formData = new FormData();
        formData.append("image", image.path);
        const result = UploadImages(formData);
        return result;
      } else {
        return image;
      }
    });

    Promise.all(results).then(async (img_results) => {
      var setImageArray = [];
      img_results?.map((image_path) => {
        setImageArray.push(image_path);
      });
      const formDataObject = {
        subject: state.subject,
        description: state.description,
        status: true,
      };
      if (files.length > 0) {
        formDataObject.ticket_images = setImageArray;
      }
      const result = await UpdateSupportTicketAPI(
        state.ticket_id,
        formDataObject
      );
      if (result.code === 200) {
        dataList("all");
        onCloseDrawer();
        setIsLoading(false);
        enqueueSnackbar(result.message, { variant: "success" });
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setIsLoading(false);
      }
    });
  };

  useEffect(() => {
    if (formType == "EDIT") {
      setState((prevState) => ({
        ...prevState,
        ["subject"]: editData?.subject,
        ["description"]: editData?.description,
        ["ticket_id"]: editData?._id,
      }));

      // The ticket's images, set fresh (pushing into state added them twice when the effect re-ran).
      const images = (editData.ticket_images || []).map((images) => ({
        thumbnail_1: images.thumbnail_1,
        thumbnail_2: images.thumbnail_2
          ? images.thumbnail_2
          : images.thumbnail_1,
        type: "image",
      }));
      setFiles(images);
      // Same objects in both lists (not copies): the update deletes `type` from them before sending,
      // exactly as before, so the saved images go out as { thumbnail_1, thumbnail_2 }.
      setOldImagesArray([...images]);
    }
  }, [formType]);

  // Tiles for the attachments (new files from the picker or images already on the ticket).
  const attachmentTiles = useComposerTiles(files);

  return (
    <Box
      component="form"
      onSubmit={formType === "ADD" ? handleSubmit : handleUpdate}
      sx={{ display: "grid", gap: gr.space[4], p: gr.space[4] }}
    >
      <GrTextField
        label="Ticket Subject"
        name="subject"
        value={state.subject}
        required={true}
        onChange={(e) => handleChange(e)}
      />
      <GrTextField
        label="Enter Description"
        multiline
        rows={5}
        name="description"
        value={state.description}
        onChange={(e) => handleChange(e)}
        required={true}
      />
      <GrAttachments
        hint="Recommended Size (1000x670)"
        files={attachmentTiles}
        onRemove={handleRemove}
        onAdd={handleUpload}
        accept="image/*,.pdf,.xlsx,.xls,.docx,.csv,.doc"
        multiple={true}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <GrButton type="submit" disabled={isLoading}>
          {isLoading
            ? formType === "ADD"
              ? "Saving..."
              : "Updating..."
            : formType === "ADD"
            ? "Save"
            : "Update"}
        </GrButton>
      </Box>
    </Box>
  );
}
